import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

function statusBadge(status) {
  if (status === 'Active') return 'bg-success';
  if (status === 'Rejected') return 'bg-danger';
  return 'bg-warning text-dark';
}

// Load the registration list with loading/error state handled outside the
// component body, so the effect callback stays branch-free.
async function loadWithState({ load, setLoading, setLoadError }) {
  setLoading(true);
  setLoadError('');
  try {
    await load();
  } catch (err) {
    setLoadError(err.message || 'Failed to load registrations.');
  } finally {
    setLoading(false);
  }
}

// Run an approve/reject decision with shared busy + success/error handling,
// keeping the try/catch out of the component's handler functions.
async function submitRegistrationDecision({
  action, refresh, setBusy, setPageMsg, successText, failText,
}) {
  setBusy(true);
  setPageMsg(null);
  try {
    const result = await action();
    // successText may depend on the response (e.g. whether an email went out).
    const msg = typeof successText === 'function' ? successText(result) : { text: successText };
    setPageMsg({ type: 'success', ...msg });
    await refresh();
  } catch (err) {
    setPageMsg({ type: 'danger', text: err.message || failText });
  } finally {
    setBusy(false);
  }
}

/** Page-level load error + action result alerts. */
function PageAlerts({ loadError, pageMsg }) {
  return (
    <>
      {loadError && (
        <div className="alert alert-danger" data-testid="approvals-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="approval-message">
          {pageMsg.text}
          {pageMsg.tempPassword && (
            <div className="mt-1 small">
              Temporary password:{' '}
              <code className="user-select-all" data-testid="approval-temp-password">
                {pageMsg.tempPassword}
              </code>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** Left column: list of registration applications. */
function RegistrationsTable({ registrations, selectedId, onSelect }) {
  return (
    <div className="col-lg-7 mb-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white pt-4 border-bottom-0">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-list-ul me-2" aria-hidden="true" />
            Registration Applications
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-secondary small text-uppercase">
                <tr>
                  <th>Company</th>
                  <th>TIN</th>
                  <th>Contact</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {registrations.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No registrations.
                    </td>
                  </tr>
                )}
                {registrations.map((r) => (
                  <RegistrationRow
                    key={r.id}
                    reg={r}
                    active={selectedId === r.id}
                    onSelect={onSelect}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A single registration row. */
function RegistrationRow({ reg, active, onSelect }) {
  return (
    <tr
      className={active ? 'table-active' : ''}
      style={{ cursor: 'pointer' }}
      onClick={() => onSelect(reg)}
      data-testid="approval-row"
      data-id={reg.id}
      data-tin={reg.tin}
    >
      <td className="fw-bold text-dark">{reg.company_name}</td>
      <td>
        <code>{reg.tin}</code>
      </td>
      <td className="small">
        {reg.contact_name}
        <span className="d-block text-muted">{reg.contact_email}</span>
      </td>
      <td>
        <span className={`badge ${statusBadge(reg.status)}`} data-testid="row-status">
          {reg.status}
        </span>
      </td>
    </tr>
  );
}

/** Read-only detail list for the selected registration. */
function RegistrationDetails({ selected }) {
  return (
    <dl className="row small mb-3">
      <dt className="col-5 text-muted">Company</dt>
      <dd className="col-7">{selected.company_name}</dd>
      <dt className="col-5 text-muted">TIN</dt>
      <dd className="col-7">
        <code data-testid="review-tin">{selected.tin}</code>
      </dd>
      <dt className="col-5 text-muted">Contact</dt>
      <dd className="col-7">{selected.contact_name}</dd>
      <dt className="col-5 text-muted">Email</dt>
      <dd className="col-7">{selected.contact_email}</dd>
      <dt className="col-5 text-muted">Phone</dt>
      <dd className="col-7">{selected.contact_phone}</dd>
      <dt className="col-5 text-muted">Billing</dt>
      <dd className="col-7">{selected.billing_address}</dd>
      <dt className="col-5 text-muted">Status</dt>
      <dd className="col-7">
        <span className={`badge ${statusBadge(selected.status)}`} data-testid="review-status">
          {selected.status}
        </span>
      </dd>
    </dl>
  );
}

/** Summary alert shown for already-decided (non-pending) registrations. */
function DecidedSummary({ selected }) {
  const decision = selected.status === 'Active' ? 'approved' : 'rejected';
  const sapNote = selected.sap_account_number ? ` SAP: ${selected.sap_account_number}.` : '';
  const reasonNote = selected.reject_reason ? ` Reason: ${selected.reject_reason}.` : '';
  return (
    <div className="alert alert-secondary small mb-0">
      This registration has been {decision}.{sapNote}{reasonNote}
    </div>
  );
}

/** Approve / Reject action area for a pending registration. */
function PendingActions({ busy, rejecting, rejectReason, onApprove, onToggleReject, onChangeReason, onConfirmReject }) {
  return (
    <>
      <div className="d-flex gap-2">
        <button
          className="btn btn-success btn-sm"
          onClick={onApprove}
          disabled={busy}
          data-testid="approve-btn"
        >
          {busy && (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          )}
          <i className="fas fa-check me-1" aria-hidden="true" />
          Approve
        </button>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={onToggleReject}
          disabled={busy}
          data-testid="reject-btn"
        >
          <i className="fas fa-times me-1" aria-hidden="true" />
          Reject
        </button>
      </div>

      {rejecting && (
        <div className="mt-3" data-testid="reject-panel">
          <label htmlFor="reject-reason" className="form-label small fw-bold text-muted">
            Rejection reason
          </label>
          <textarea
            id="reject-reason"
            rows={2}
            className="form-control form-control-sm mb-2"
            value={rejectReason}
            onChange={(e) => onChangeReason(e.target.value)}
            data-testid="reject-reason"
          />
          <button
            className="btn btn-danger btn-sm"
            onClick={onConfirmReject}
            disabled={busy}
            data-testid="confirm-reject"
          >
            Confirm Rejection
          </button>
        </div>
      )}
    </>
  );
}

/** Right column: review + approval panel for the selected registration. */
function ReviewPanel({
  selected, isPending, sapId, sapError, onChangeSapId,
  busy, rejecting, rejectReason, onApprove, onToggleReject, onChangeReason, onConfirmReject,
}) {
  return (
    <div className="col-lg-5 mb-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white pt-4 border-bottom-0">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-clipboard-check me-2" aria-hidden="true" />
            Review &amp; Approval
          </h5>
        </div>
        <div className="card-body">
          {!selected ? (
            <p className="text-muted small mb-0">Select a registration to review.</p>
          ) : (
            <div data-testid="review-panel">
              <RegistrationDetails selected={selected} />

              <div className="mb-3">
                <label htmlFor="sap-id" className="form-label small fw-bold text-muted">
                  SAP Customer ID
                </label>
                <input
                  id="sap-id"
                  className={`form-control form-control-sm${sapError ? ' is-invalid' : ''}`}
                  value={sapId}
                  onChange={(e) => onChangeSapId(e.target.value)}
                  disabled={!isPending}
                  data-testid="sap-customer-id"
                />
                {sapError && <div className="invalid-feedback">{sapError}</div>}
              </div>

              {isPending ? (
                <PendingActions
                  busy={busy}
                  rejecting={rejecting}
                  rejectReason={rejectReason}
                  onApprove={onApprove}
                  onToggleReject={onToggleReject}
                  onChangeReason={onChangeReason}
                  onConfirmReject={onConfirmReject}
                />
              ) : (
                <DecidedSummary selected={selected} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Admin Approvals Console (UC fc26e924): review pending customer registrations,
 * verify TIN/billing, map to a SAP Customer ID, and Approve (activate + welcome
 * email) or Reject (with a reason that notifies the applicant).
 */
export default function CustomerApprovalsPage() {
  const [registrations, setRegistrations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [sapId, setSapId] = useState('');
  const [sapError, setSapError] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.listCustomerRegistrations();
    setRegistrations(res || []);
    return res || [];
  }, []);

  useEffect(() => {
    loadWithState({ load, setLoading, setLoadError });
  }, [load]);

  const select = (reg) => {
    setPageMsg(null);
    setSapError('');
    setRejecting(false);
    setRejectReason('');
    setSelected(reg);
    setSapId(reg.sap_account_number || '');
  };

  const refreshKeepingSelection = async (id) => {
    const list = await load();
    const fresh = list.find((r) => r.id === id);
    if (fresh) setSelected(fresh);
  };

  const approve = async () => {
    if (!selected) return;
    if (!sapId.trim()) {
      setSapError('SAP Customer ID is required to approve');
      return;
    }
    setSapError('');
    await submitRegistrationDecision({
      action: () => api.approveRegistration(selected.id, sapId.trim()),
      refresh: () => refreshKeepingSelection(selected.id),
      setBusy,
      setPageMsg,
      // Approval creates the customer's login and emails a temporary password
      // (8818); say whether the email actually went out.
      successText: (res) => {
        if (res?.existingLogin) {
          return { text: 'Account approved and activated — the contact already has a login.' };
        }
        return {
          text: res?.emailSent
            ? 'Account approved and activated — login details emailed to the customer.'
            : 'Account approved and activated — the login email could not be sent; share the temporary password with the customer.',
          tempPassword: res?.tempPassword,
        };
      },
      failText: 'Failed to approve.',
    });
  };

  const submitReject = async () => {
    if (!selected) return;
    if (!rejectReason.trim()) {
      setPageMsg({ type: 'danger', text: 'A rejection reason is required.' });
      return;
    }
    await submitRegistrationDecision({
      action: () => api.rejectRegistration(selected.id, rejectReason.trim()),
      refresh: async () => {
        setRejecting(false);
        setRejectReason('');
        await refreshKeepingSelection(selected.id);
      },
      setBusy,
      setPageMsg,
      successText: 'Registration rejected — applicant notified.',
      failText: 'Failed to reject.',
    });
  };

  const isPending = selected && selected.status === 'Pending Admin Approval';

  return (
    <div className="container-fluid py-4" data-testid="approvals-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-user-shield me-2" aria-hidden="true" />
                Customer Approvals Console
              </h3>
              <p className="text-muted mb-0 small">
                Review pending registrations, map to SAP, and activate or reject accounts.
              </p>
            </div>
            <span className="badge bg-primary p-2">Admin Review</span>
          </div>
        </div>
      </div>

      <PageAlerts loadError={loadError} pageMsg={pageMsg} />

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          <RegistrationsTable
            registrations={registrations}
            selectedId={selected?.id}
            onSelect={select}
          />
          <ReviewPanel
            selected={selected}
            isPending={isPending}
            sapId={sapId}
            sapError={sapError}
            onChangeSapId={(v) => {
              setSapId(v);
              setSapError('');
            }}
            busy={busy}
            rejecting={rejecting}
            rejectReason={rejectReason}
            onApprove={approve}
            onToggleReject={() => setRejecting((v) => !v)}
            onChangeReason={setRejectReason}
            onConfirmReject={submitReject}
          />
        </div>
      )}
    </div>
  );
}
